import React, { useState, useEffect } from 'react';
import ItinerariesSvg, {
  ItinerariesSvgType
} from '../itineraries/itineraries-svg';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import ActivityAppearanceService from '../../services/activity-appearance.service';
import { ACTIVITY_ICONS } from '../../constants/activity-icons.constants';
import tripActivityService from '../../services/trips/trip-activity.service';
import { BaseItemCard } from '../shared/item-card/base-item-card';
import { formatPhotoUrls } from '../../utils/photo-utils';

export type CustomInfoWindowProps = {
  name: string;
  type?: string;
  notes?: string[];
  id?: string;
  tripId: string;
  folders?: string[];
  placesId?: string;
  latitude?: number;
  longitude?: number;
  websiteUrl?: string;
  photos?: string[];
  activity?: ITripActivity; // Pass full activity object to avoid fetching
  onSave?: () => void; // Callback to refresh map after save
  onPreviewChange?: (color: string, icon: string) => void; // Callback for live preview
};

const CustomInfoWindow: React.FC<CustomInfoWindowProps> = ({
  id,
  tripId,
  name,
  notes,
  websiteUrl,
  photos = [],
  activity: activityProp,
  onSave,
  onPreviewChange
}) => {
  const [activity, setActivity] = useState<ITripActivity | null>(
    activityProp || null
  );
  const [selectedColor, setSelectedColor] = useState('#842234');
  const [selectedIconType, setSelectedIconType] = useState('restaurant');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  // Initialize from passed activity prop
  useEffect(() => {
    if (activityProp) {
      setActivity(activityProp);

      // Initialize color and icon from activity using ActivityAppearanceService
      const effectiveColor = ActivityAppearanceService.getColor(activityProp);
      const effectiveIcon = ActivityAppearanceService.getIcon(activityProp);

      setSelectedColor(effectiveColor);
      setSelectedIconType(effectiveIcon);
    }
  }, [activityProp]);

  const colors = [
    '#842234',
    '#FF6B6B',
    '#4169E1',
    '#10B981',
    '#F59E0B',
    '#A855F7',
    '#EC4899',
    '#14B8A6',
    '#EF4444',
    '#8B5CF6'
  ];

  // Use ACTIVITY_ICONS constant for consistency with backend
  const iconTypes = ACTIVITY_ICONS.map((icon) => ({
    id: icon,
    type: icon,
    label: icon.charAt(0).toUpperCase() + icon.slice(1)
  }));

  // Handle color change with preview
  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    setHasChanges(true);
    setSaveSuccess(false);
    // Trigger preview on map
    if (onPreviewChange) {
      onPreviewChange(color, selectedIconType);
    }
  };

  // Handle icon change with preview
  const handleIconChange = (icon: string) => {
    setSelectedIconType(icon);
    setHasChanges(true);
    setSaveSuccess(false);
    // Trigger preview on map
    if (onPreviewChange) {
      onPreviewChange(selectedColor, icon);
    }
  };

  // Reset to default appearance (clear customization)
  const handleResetToDefault = () => {
    if (!activity) return;

    // Get type defaults
    const defaultColor = ActivityAppearanceService.getColor({
      type: activity.type
    });
    const defaultIcon = ActivityAppearanceService.getIcon({
      type: activity.type
    });

    setSelectedColor(defaultColor);
    setSelectedIconType(defaultIcon);
    setHasChanges(true);
    setSaveSuccess(false);

    // Trigger preview on map
    if (onPreviewChange) {
      onPreviewChange(defaultColor, defaultIcon);
    }
  };

  // Save appearance to database
  const handleSaveAppearance = async () => {
    if (!id || !tripId || !activity) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      console.log('[CustomInfoWindow] Saving appearance...', {
        id,
        tripId,
        selectedColor,
        selectedIconType
      });

      // Check if we're resetting to defaults
      const defaultColor = ActivityAppearanceService.getColor({
        type: activity.type
      });
      const defaultIcon = ActivityAppearanceService.getIcon({
        type: activity.type
      });
      const isResettingToDefaults =
        selectedColor === defaultColor && selectedIconType === defaultIcon;

      console.log('[CustomInfoWindow] Defaults check:', {
        defaultColor,
        defaultIcon,
        isResettingToDefaults
      });

      // Update activity with customization or null to clear
      const updatedActivity = await tripActivityService.updateActivity(
        tripId,
        id,
        {
          id,
          tripId,
          title: activity.title,
          description: activity.description || '',
          websiteUrl: activity.websiteUrl,
          isPublic: false,
          imageUrl: activity.imageUrl || 'default.png',
          notes: activity.notes,
          customization: isResettingToDefaults
            ? null
            : {
                color: selectedColor,
                icon: selectedIconType
              }
        }
      );

      console.log('[CustomInfoWindow] Save successful:', updatedActivity);

      setActivity(updatedActivity);
      setHasChanges(false);
      setSaveSuccess(true);

      // Notify parent to refresh map
      if (onSave) {
        onSave();
      }

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('[CustomInfoWindow] Error saving appearance:', error);
      setSaveError(
        error?.message || 'Failed to save appearance. Please try again.'
      );

      // Auto-hide error message after 5 seconds
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUrlClick = () => {
    if (websiteUrl) {
      window.open(websiteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDelete = () => {
    // TODO: Implement delete logic
    console.log('Delete clicked');
  };

  return (
    <div className='custom-info-window'>
      <div className='info-window-content'>
        {/* Action Buttons */}
        <div className='action-buttons'>
          {websiteUrl && (
            <button
              className='btn-website'
              onClick={handleUrlClick}
            >
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' />
              </svg>
              Website
            </button>
          )}
          <button
            className={`btn-booked ${isBooked ? 'active' : ''}`}
            onClick={() => setIsBooked(!isBooked)}
          >
            {isBooked && (
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
              </svg>
            )}
            Booked
          </button>
          <button
            className='btn-delete'
            onClick={handleDelete}
          >
            <svg
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='currentColor'
            >
              <path d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z' />
            </svg>
            Delete
          </button>
        </div>

        {/* Customization Section with Border */}
        <div
          style={{
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px',
            backgroundColor: '#F9FAFB'
          }}
        >
          {/* Pin Color Section */}
          <div className='section'>
            <label className='section-label'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='currentColor'
                style={{ marginRight: '6px' }}
              >
                <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
              </svg>
              Pin Color
            </label>
            <div className='color-picker'>
              {colors.map((color) => (
                <button
                  key={color}
                  className={`color-option ${selectedColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(color)}
                />
              ))}
            </div>
          </div>

          {/* Icon Type Section */}
          <div className='section'>
            <label className='section-label'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='currentColor'
                style={{ marginRight: '6px' }}
              >
                <path d='M12 2l-5.5 9h11z' />
                <circle
                  cx='17.5'
                  cy='17.5'
                  r='4.5'
                />
                <path d='M3 13.5h8v8H3z' />
              </svg>
              Icon Type
            </label>
            <div className='icon-type-picker'>
              {iconTypes.map((iconItem) => (
                <button
                  key={iconItem.id}
                  className={`icon-option ${selectedIconType === iconItem.id ? 'selected' : ''}`}
                  onClick={() => handleIconChange(iconItem.id)}
                  title={iconItem.label}
                >
                  <ItinerariesSvg
                    type={iconItem.type as any}
                    fill='#2D2D2D'
                    style={{ width: '16px', height: '16px' }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Save/Reset Buttons */}
          <div
            className='section mt-2'
            style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}
          >
            {saveSuccess && (
              <div
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#D1FAE5',
                  border: '1px solid #10B981',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#065F46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
                </svg>
                Appearance saved successfully!
              </div>
            )}

            {saveError && (
              <div
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #EF4444',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#991B1B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' />
                </svg>
                {saveError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className='btn-reset-appearance'
                onClick={handleResetToDefault}
                disabled={isSaving}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  color: '#374151',
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                Reset to Default
              </button>

              <button
                className='btn-save-appearance'
                onClick={handleSaveAppearance}
                disabled={!hasChanges || isSaving}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  backgroundColor:
                    hasChanges && !isSaving ? '#842234' : '#D1D5DB',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                {isSaving ? (
                  <>
                    <svg
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      style={{ animation: 'spin 1s linear infinite' }}
                    >
                      <circle
                        cx='12'
                        cy='12'
                        r='10'
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Appearance'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Activity Card Display */}
        {activity && activity.id && (
          <div style={{ marginBottom: '12px' }}>
            <BaseItemCard
              id={activity.id}
              title={activity.title}
              description={activity.description}
              websiteUrl={activity.websiteUrl}
              activityType={activity.type}
              icon={
                activity.type ? (
                  <ItinerariesSvg
                    type={
                      activity.type.endsWith('s')
                        ? (activity.type
                            .slice(0, -1)
                            .toLowerCase() as ItinerariesSvgType)
                        : (activity.type.toLowerCase() as ItinerariesSvgType)
                    }
                    fill={selectedColor}
                    style={{ width: 16, height: 16, opacity: 0.8 }}
                  />
                ) : undefined
              }
              iconColor={selectedColor}
              thumbnailUrl={activity.imageUrl}
              photos={
                activity.photos
                  ? formatPhotoUrls(activity.photos)
                  : activity.imageUrl
                    ? [activity.imageUrl]
                    : undefined
              }
              commentMetadata={activity.commentMetadata}
              selectable={false}
              draggable={false}
              layout='standard'
              orientation='vertical'
              hoverable={false}
              mapped={!!activity.googlePlaceId}
              unmappedLabel='Not mapped'
              showContent={false}
              hidePhotos={false}
              showFooter={true}
              showWebsiteUrl={true}
              showMessageButton={false}
              showDeleteButton={false}
              showOptionsMenu={false}
            />
          </div>
        )}

        {/* Address Section */}
        {notes && notes.length > 0 && (
          <div className='section'>
            <label className='form-label'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='currentColor'
                style={{ marginRight: '6px' }}
              >
                <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
              </svg>
              Address
            </label>
            <p className='address-text'>{notes[0]}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomInfoWindow;
